/**
 * `matching` — attach each entry on one side to an entry on the other.
 *
 * Added because a memory-game asset shipped ungraded for want of it, and because the old
 * library's Dot2Dot is one of the most-used activities there. Its grading is the model
 * followed here: it scored `correct × 100 ÷ total` and reported a per-pair mark, so partial
 * credit and per-pair granularity are the established behaviour rather than an invention.
 *
 * Each left is judged on its own. That is what makes distractors on the right, and several
 * lefts sharing one right, work without a flag for either — nothing here counts how many
 * times a right was used, because nothing about correctness depends on it.
 */

import { isMatchingResponse } from '@cet/asset-contract';
import type { ItemResponse, ItemVerdict, Locale, MatchingSchemeDoc, VerdictPart } from '@cet/asset-contract';

import { ScoringError } from '../errors.js';
import { graded, unanswered } from '../verdicts.js';

export function scoreMatching(
  itemId: string,
  scheme: MatchingSchemeDoc,
  response: ItemResponse | null | undefined,
  locale: Locale,
): ItemVerdict {
  if (response === null || response === undefined) {
    return unanswered(itemId, scheme.points);
  }

  if (!isMatchingResponse(response)) {
    throw new ScoringError(
      'invalid-response',
      `Item "${itemId}" is a matching item, so its response must be ` +
        `{ pairs: { leftId: rightId } }. Received keys: ` +
        `${Object.keys(response).join(', ') || '(none)'}.`,
      `responses.${itemId}`,
    );
  }

  // Nothing connected is a blank, not a wrong answer (D32).
  if (Object.keys(response.pairs).length === 0) {
    return unanswered(itemId, scheme.points);
  }

  // Sorted, so nothing about the result can depend on the order the keys were written in:
  // the browser and the server have to agree byte for byte.
  const leftIds = Object.keys(scheme.correct).sort();

  let matched = 0;
  /** One per graded left-hand entry, keyed by the id the asset drew on that row. */
  const parts: VerdictPart[] = [];
  for (const leftId of leftIds) {
    // An absent left is an unconnected one, which is why the response has no null sentinel
    // to distinguish from a wrong connection — both simply fail to equal the answer.
    const right = response.pairs[leftId] === scheme.correct[leftId];
    if (right) matched += 1;
    parts.push({ id: leftId, correct: right });
  }

  const extra = Object.keys(response.pairs).filter((leftId) => scheme.correct[leftId] === undefined);
  // A connection from a left the scheme does not grade means the learner saw a different
  // item than this scheme describes, so the sequence cannot be called wholly right even if
  // every graded pair matched.
  const whollyCorrect = matched === leftIds.length && extra.length === 0;

  const awarded =
    (scheme.credit ?? 'all') === 'partial'
      ? // Integer arithmetic — scores never accumulate as floats — so this floors, and a
        // wholly correct answer lands on the full points exactly.
        Math.floor((scheme.points * matched) / leftIds.length)
      : whollyCorrect
        ? scheme.points
        : 0;

  return graded(
    itemId,
    { awarded: whollyCorrect ? scheme.points : awarded, max: scheme.points },
    scheme.feedback,
    whollyCorrect,
    locale,
    parts,
  );
}
